/**
 * useConversations — приватная история диалогов пользователя.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { useAiStore, type AiRole } from '@/state/aiStore';

export interface ConversationSummary {
  id: string;
  title: string;
  updated_at: string;
}

interface ServerMessage {
  id: string;
  role: AiRole;
  content: string;
  tool_calls: unknown;
}

export function useConversations() {
  return useQuery<ConversationSummary[]>({
    queryKey: ['conversations'],
    queryFn: () => apiFetch<ConversationSummary[]>('/api/conversations'),
    staleTime: 10_000,
  });
}

/** Хелперы открыть/удалить диалог + инвалидация списка. */
export function useConversationActions() {
  const qc = useQueryClient();
  const hydrate = useAiStore((s) => s.hydrate);
  const setConversationId = useAiStore((s) => s.setConversationId);
  const newChat = useAiStore((s) => s.newChat);

  const open = async (id: string) => {
    const msgs = await apiFetch<ServerMessage[]>(`/api/conversations/${id}`);
    setConversationId(id);
    hydrate(msgs);
  };

  const remove = async (id: string) => {
    await apiFetch(`/api/conversations/${id}`, { method: 'DELETE' });
    if (useAiStore.getState().conversationId === id) newChat();
    await qc.invalidateQueries({ queryKey: ['conversations'] });
  };

  const refresh = () => qc.invalidateQueries({ queryKey: ['conversations'] });

  return { open, remove, refresh, newChat };
}
